<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">⚠️</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
        <template slot-scope="{ row }" slot="dataTime">
          <span class="time-cell">{{ formatDateTime(row.dataTime) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="time">
          <span class="time-cell">{{ row.time || '-' }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="type">
          <span class="data-cell">{{ row.type || '-' }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="acceleration">
          <span class="data-cell value">{{ formatValue(row.acceleration) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="accelerationX">
          <span class="data-cell value">{{ formatValue(row.accelerationX, true) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="accelerationY">
          <span class="data-cell value">{{ formatValue(row.accelerationY, true) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="accelerationZ">
          <span class="data-cell value">{{ formatValue(row.accelerationZ, true) }}</span>
        </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'

export default {
  name: 'CollisionHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    columns() {
      return [
        {
          title: langMap.dashboard_dataTime[this.lang],
          slot: 'dataTime',
          align: 'center',
          width: 180
        },
        {
          title: langMap.dashboard_collisionTime[this.lang],
          slot: 'time',
          align: 'center',
          width: 180
        },
        {
          title: langMap.dashboard_collisionType[this.lang],
          slot: 'type',
          align: 'center'
        },
        {
          title: `${langMap.dashboard_collisionAcceleration[this.lang]} (g)`,
          slot: 'acceleration',
          align: 'center'
        },
        {
          title: `${langMap.dashboard_collisionAccelerationX[this.lang]} (g)`,
          slot: 'accelerationX',
          align: 'center'
        },
        {
          title: `${langMap.dashboard_collisionAccelerationY[this.lang]} (g)`,
          slot: 'accelerationY',
          align: 'center'
        },
        {
          title: `${langMap.dashboard_collisionAccelerationZ[this.lang]} (g)`,
          slot: 'accelerationZ',
          align: 'center'
        }
      ]
    },
    totalPages() {
      return this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total)
      return `${start}-${end}`
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.pagination.current
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    // 格式化数值
    formatValue(value, divideByTen = false) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      
      const num = parseFloat(value)
      if (isNaN(num)) {
        return value
      }
      
      // 如果需要除以10
      const finalValue = divideByTen ? num / 10 : num
      
      // 保留2位小数
      return finalValue.toFixed(2)
    },
    
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      
      // 如果已经是格式化后的字符串，直接返回
      if (typeof timestamp === 'string' && timestamp.includes('-')) {
        return timestamp
      }
      
      // 否则将时间戳转换为日期格式
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },
    
    // 计算表格高度
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 120
      const paginationHeight = 60
      const padding = 40
      this.tableHeight = windowHeight - headerHeight - paginationHeight - padding - 281
      if (this.tableHeight < 300) {
        this.tableHeight = 300
      }
    },
    
    // 分页处理
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    
    handlePageSizeChange(size) {
      this.$emit('size-change', size)
    },
    
    // 快速跳转页面
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(
          langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages)
        )
        this.jumpPage = null
        return
      }
      this.handlePageChange(this.jumpPage)
      this.jumpPage = null
    }
  }
}
</script>

<style lang="less" scoped>
// 数据单元格样式
.data-cell {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #00f0ff;
  text-shadow: 0 0 5px rgba(0, 240, 255, 0.6);
  
  &.value {
    color: #39FF14;
    text-shadow: 0 0 5px rgba(57, 255, 20, 0.6);
  }
}

.time-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}
</style>
